/**
 * ============================================================================
 * DEV-MODE MOCK-AUTH HARNESS  (Phase 4c)
 * ============================================================================
 * Seeds enough mock state — gated entirely behind `DEV_MODE` — that the app
 * can run end-to-end in `VITE_DEV_MODE=true` mode with NO backend.
 *
 * `seedDevData()` writes the `dev_*` localStorage keys the existing api.ts
 * DEV_MODE branches already read. The app's normal fetch flow (App.tsx) then
 * hydrates the workspace / preferences stores from them.
 *
 * EVERYTHING here is dead code in production: every export early-returns when
 * `DEV_MODE` is false (a build-time-constant `false`, so the bodies are
 * tree-shaken). Nothing in this module changes production behaviour.
 * ============================================================================
 */
import { DEV_MODE, MOCK_USER } from '@/lib/devMode';
import {
  DEV_KEYS,
  buildDevCollections,
  buildDevFacts,
  buildDevKeywords,
  buildDevPosts,
  buildDevSessions,
  buildDevSocialAccounts,
  devWrite,
  installDevHarness,
} from '@/lib/devFixtures';

// Stable workspace id for every dev_* key.
const WORKSPACE_ID = 'ws-dev-1';
const now = () => new Date().toISOString();

// ── Mock workspace (rich: brand DNA + profile fields, onboarded) ────────────
const MOCK_WORKSPACE = {
  id: WORKSPACE_ID,
  name: 'Northwind Studio',
  logo_url: null,
  company_url: 'https://northwind.studio',
  company_name: 'Northwind Studio',
  industry: 'B2B SaaS — design tooling',
  description:
    'We build collaborative design-system tooling for product teams — a single source of truth for tokens, components, and docs.',
  target_audience:
    'Design-system leads and senior product designers at 50–500 person software companies.',
  brand_voice: 'Direct, builder-to-builder, lightly witty. No corporate fluff.',
  default_tone: 'professional',
  // Visual Brand DNA
  brand_colors: ['#6D28D9', '#0EA5E9', '#111827'],
  primary_color: '#6D28D9',
  visual_style: 'Clean, high-contrast, generous whitespace',
  photography_style: 'Candid team-at-work shots, soft daylight',
  visual_mood: 'Focused, modern, optimistic',
  brand_fonts: ['Inter', 'Lato'],
  logo_description: 'A compass rose mark in violet',
  extracted_logo_url: null,
  visual_brand_analyzed: true,
  // Company Voice (full settings live under dev_ws_voice_<id>)
  brand_voice_settings: null,
  workspace_type: 'company' as const,
  company_stage: 'growing' as const,
  // Metadata
  is_default: true,
  is_onboarded: true,
  created_at: now(),
  updated_at: now(),
  deleted_at: null,
};

// ── Mock services (≥2 so services-card / services-edit-button resolve) ──────
const MOCK_WS_SERVICES = [
  {
    id: 'svc-dev-1',
    workspace_id: WORKSPACE_ID,
    name: 'Design System Platform',
    description: 'Hosted token + component source of truth with live docs.',
    display_order: 0,
    is_active: true,
    created_at: now(),
    updated_at: now(),
  },
  {
    id: 'svc-dev-2',
    workspace_id: WORKSPACE_ID,
    name: 'Migration Service',
    description: 'Hands-on help moving a legacy UI library onto the platform.',
    display_order: 1,
    is_active: true,
    created_at: now(),
    updated_at: now(),
  },
];

// ── Mock voice (tone + ≥3 samples + analysis so voice-extracted resolves) ───
const MOCK_WS_VOICE = {
  voice_tone: ['Confident', 'Friendly'],
  writing_samples: [
    "We shipped tokens-as-code this week. Here's what broke and how we fixed it.",
    'Most design systems die in a Figma file nobody opens. Ours lives where engineers already are.',
    "Stop hand-syncing colors. Define them once, consume them everywhere. That's the whole pitch.",
  ],
  founder_story:
    'Started Northwind after watching three teams rebuild the same button for the fourth time.',
  key_phrases_use: ['source of truth', 'tokens-as-code', 'ship it'],
  key_phrases_avoid: ['leverage', 'synergy', 'best-in-class'],
  content_themes: ['design ops', 'developer experience', 'shipping culture'],
  anti_patterns: ['humble-bragging', 'engagement-bait questions'],
  // Returned by api.getCompanyVoice via spread → CompanyVoiceTab shows the
  // Voice Profile card (data-tour="voice-extracted" / "voice-analyze-button").
  voice_analysis: {
    sentence_style: {
      avg_length: 'short',
      uses_fragments: true,
      uses_questions: true,
      paragraph_length: 'short',
    },
    vocabulary_level: 'simple',
    perspective: 'first_person',
    formatting_preferences: {
      uses_emojis: false,
      uses_bullets: false,
      uses_bold: true,
      uses_hashtags: false,
    },
    content_patterns: {
      leads_with_data: true,
      uses_stories: true,
      uses_examples: true,
      asks_audience: true,
    },
    signature_phrases: ['Here is what broke...', 'The whole pitch is...'],
    words_they_use: ['shipped', 'built', 'broke', 'fixed'],
    words_they_avoid: ['leverage', 'synergy'],
    overall_vibe: 'Punchy, candid builder sharing real lessons.',
    analyzed_at: now(),
    samples_hash: 'dev-mock',
  },
};

// ── Mock personas (≥2 so personas-card / personas-edit-button resolve) ──────
const MOCK_WS_PERSONAS = [
  {
    id: 'persona-dev-1',
    workspace_id: WORKSPACE_ID,
    name: 'Priya — Design Systems Lead',
    description: 'Owns the design system at a 200-person SaaS company.',
    age_range: '30-40',
    job_titles: ['Design Systems Lead', 'Staff Product Designer'],
    seniority_level: 'senior',
    industries: ['Software', 'B2B SaaS'],
    location_tendency: 'Hybrid, major tech hubs',
    motivations: ['Consistency at scale', 'Less rework for her team'],
    frustrations: ['Designers and engineers drifting out of sync', 'No single source of truth'],
    values: ['Craft', 'Pragmatism'],
    aspirations: ['Be the team that ships fast without breaking the brand'],
    is_primary: true,
    created_at: now(),
    updated_at: now(),
  },
  {
    id: 'persona-dev-2',
    workspace_id: WORKSPACE_ID,
    name: 'Marcus — Frontend Eng Manager',
    description: 'Leads a frontend platform team; cares about DX.',
    age_range: '35-45',
    job_titles: ['Engineering Manager', 'Frontend Platform Lead'],
    seniority_level: 'manager',
    industries: ['Software'],
    location_tendency: 'Remote-first',
    motivations: ['Fewer UI bugs', 'Faster onboarding for new devs'],
    frustrations: ['Copy-pasted components', 'Tokens living in a doc nobody updates'],
    values: ['Reliability', 'Developer experience'],
    aspirations: ['A component library engineers actually trust'],
    is_primary: false,
    created_at: now(),
    updated_at: now(),
  },
];

// ── Mock preferences (fresh: tour auto-fires) ───────────────────────────────
const MOCK_PREFERENCES = {
  default_tone: 'professional',
  default_platforms: ['linkedin', 'instagram'],
  email_notifications: true,
  marketing_emails: false,
  enter_to_send: false,
  has_completed_tutorial: false,
  tutorial_dismissed_at: null as string | null,
};

/**
 * Write all dev_* localStorage keys. Always overwrites when DEV_MODE so every
 * page load starts from a known, tour-ready state (lets a refresh act as a full
 * reset for QA). No-op in production.
 *
 * The content keys (posts, collections, facts, history, connections) are seeded
 * from `devFixtures` so the app is LIVED-IN on first load: My Posts, the Feed,
 * Collections, the Fact Bank, Connections and the tool's History panel all have
 * something in them. They used to be permanently blank in dev because the api.ts
 * mocks behind them were a hardcoded `return []` — which is how four real bugs
 * survived a whole session of "verification" on surfaces nobody could see.
 *
 * Mutations (save a post, favourite it, delete a collection) write back to these
 * same keys, so a change is real for the rest of the session and a reload resets
 * it. That reset is deliberate: it keeps QA runs deterministic.
 */
export function seedDevData(): void {
  if (!DEV_MODE) return;
  try {
    localStorage.setItem('dev_user', JSON.stringify(MOCK_USER));
    localStorage.setItem('dev_preferences', JSON.stringify(MOCK_PREFERENCES));
    localStorage.setItem('dev_workspaces', JSON.stringify([MOCK_WORKSPACE]));
    localStorage.setItem('dev_active_workspace_id', WORKSPACE_ID);
    localStorage.setItem(`dev_ws_services_${WORKSPACE_ID}`, JSON.stringify(MOCK_WS_SERVICES));
    localStorage.setItem(`dev_ws_voice_${WORKSPACE_ID}`, JSON.stringify(MOCK_WS_VOICE));
    localStorage.setItem(`dev_ws_personas_${WORKSPACE_ID}`, JSON.stringify(MOCK_WS_PERSONAS));
    localStorage.setItem('dev_onboarding_completed', 'true');

    // Content — the surfaces that were permanently empty in dev.
    devWrite(DEV_KEYS.posts, buildDevPosts());
    devWrite(DEV_KEYS.collections, buildDevCollections());
    devWrite(DEV_KEYS.facts, buildDevFacts());
    devWrite(DEV_KEYS.sessions, buildDevSessions());
    devWrite(DEV_KEYS.social, buildDevSocialAccounts());
    devWrite(DEV_KEYS.keywords, buildDevKeywords());
  } catch {
    // localStorage unavailable (private mode etc.) — dev-only, safe to ignore.
  }
}

/** Attach `window.__zupaDev` (fault injection + latency + reseed). No-op in prod. */
export function installDevApi(): void {
  installDevHarness(seedDevData);
}
